import { isDateFilterEmpty } from "@/components/date-filter/date-filter.utils.ts";
import {
  ApiEmployeeColumn,
  ApiEmployeeColumnFilter,
  ApiEmployeeColumnFilters,
  groupColumnFilters,
} from "@/types/employees/employee-column.types.ts";
import type { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Параметр URL с фильтрами по колонкам. Набор полей динамический и приходит из
 * каталога, поэтому плоские параметры (`department_ids=12,15`) здесь не годятся:
 * состояние лежит одним JSON-значением.
 */
export const COLUMN_FILTERS_PARAM = "cf";
export const LIFECYCLE_STATUS_COLUMN = "employee.lifecycle_status";
export const IS_MANAGER_COLUMN = "employee.position.is_manager";

/** Пустой фильтр не отправляется: у бэка это не «пусто», а отсутствие условия. */
export function isColumnFilterEmpty(filter: ApiEmployeeColumnFilter): boolean {
  if (filter.field === LIFECYCLE_STATUS_COLUMN && filter.option_ids !== undefined) return false;
  if (filter.is_empty !== undefined) return false;
  if (filter.switch !== undefined) return false;
  // Бэк отбрасывает текст из одних пробелов, поэтому и здесь он не считается
  if (filter.text?.trim()) return false;
  if (filter.text_array?.length) return false;
  if (filter.option_ids?.length) return false;
  if (filter.employee_ids?.length) return false;
  if (filter.number_from !== undefined || filter.number_to !== undefined) return false;
  if (filter.datetime_from || filter.datetime_to) return false;
  if (filter.date_filter && !isDateFilterEmpty(filter.date_filter)) return false;
  return true;
}

/**
 * Фильтры, которые реально уходят в запрос: со значением и по колонке, которая
 * есть в каталоге зрителя и фильтруется. Колонка пропадает из каталога вместе
 * с правом или полем, а такой ключ из URL или сохранённой вьюхи бэк отвергает
 * с 422 (`employee_column_not_available` / `employee_column_not_filterable`).
 * Пока каталог не пришёл, проверять не по чему — запрос и так ждёт каталог.
 */
export function selectActiveColumnFilters(
  filters: ApiEmployeeColumnFilter[],
  columnByKey?: ReadonlyMap<string, ApiEmployeeColumn>
): ApiEmployeeColumnFilter[] {
  return filters.filter((filter) => {
    if (isColumnFilterEmpty(filter)) return false;
    if (!columnByKey?.size) return true;
    return columnByKey.get(filter.field)?.is_filterable ?? false;
  });
}

/**
 * Статус уходит старым параметром `employee_lifecycle_statuses`: только он
 * заменяет статусы организации по умолчанию (не передан — статусы
 * организации, `[]` — все статусы). Колонкой в `personal_filter` статус
 * накладывался бы на них через AND и не мог бы их расширить.
 */
export function splitLifecycleStatusFilter(filters: ApiEmployeeColumnFilter[]): {
  employee_lifecycle_statuses: ApiEmployeeLifecycleStatus[] | undefined;
  columnFilters: ApiEmployeeColumnFilter[];
} {
  const lifecycle = filters.find((filter) => filter.field === LIFECYCLE_STATUS_COLUMN);
  return {
    employee_lifecycle_statuses: lifecycle
      ? ((lifecycle.option_ids ?? []) as ApiEmployeeLifecycleStatus[])
      : undefined,
    columnFilters: filters.filter((filter) => filter.field !== LIFECYCLE_STATUS_COLUMN),
  };
}

function parseFilters(raw: string | null): ApiEmployeeColumnFilter[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Элемент без ключа колонки бэк отвергает с 422: чужой мусор в URL отбрасываем
    return parsed.filter(
      (item): item is ApiEmployeeColumnFilter =>
        Boolean(item) && typeof item === "object" && typeof item.field === "string"
    );
  } catch {
    return [];
  }
}

/**
 * Фильтры по колонкам каталога: состояние в URL и раскладка по семи спискам
 * тела запроса. Старые плоские фильтры живут рядом и этим хуком не трогаются.
 */
export const useEmployeeColumnFilters = (columnByKey?: ReadonlyMap<string, ApiEmployeeColumn>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => parseFilters(searchParams.get(COLUMN_FILTERS_PARAM)),
    [searchParams]
  );

  const write = useCallback(
    (next: ApiEmployeeColumnFilter[]) => {
      setSearchParams((current) => {
        const params = new URLSearchParams(current);
        if (next.length) {
          params.set(COLUMN_FILTERS_PARAM, JSON.stringify(next));
        } else {
          params.delete(COLUMN_FILTERS_PARAM);
        }
        // Изменение условий сбрасывает страницу: на прежней может не быть строк
        params.delete("page");
        return params;
      });
    },
    [setSearchParams]
  );

  /** Полная замена набора: панель применяет свои правки одним действием. */
  const setFilters = useCallback(
    (next: ApiEmployeeColumnFilter[]) => {
      // Фильтр без значения в URL не нужен: он ничего не сужает и при
      // следующем открытии панели восстановится пустым
      write(next.filter((filter) => !isColumnFilterEmpty(filter)));
    },
    [write]
  );

  const clearFilters = useCallback(() => write([]), [write]);

  const activeFilters = useMemo(
    () => selectActiveColumnFilters(filters, columnByKey),
    [filters, columnByKey]
  );

  const lifecycleSplit = useMemo(() => splitLifecycleStatusFilter(activeFilters), [activeFilters]);

  /**
   * Списки для тела запроса. Фильтры без значения не уезжают: колонка уже
   * добавлена в панель, но условие ещё не задано. Статус в списки не входит —
   * он уходит в `employee_lifecycle_statuses`.
   */
  const requestFilters = useMemo<ApiEmployeeColumnFilters>(
    () => groupColumnFilters(lifecycleSplit.columnFilters),
    [lifecycleSplit.columnFilters]
  );

  return {
    filters,
    activeFilters,
    requestFilters,
    employee_lifecycle_statuses: lifecycleSplit.employee_lifecycle_statuses,
    activeCount: activeFilters.length,
    setFilters,
    clearFilters,
  };
};
